import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";

const kb = (size) => (size > 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${Math.round(size / 1024)} KB`);

export function DocumentsPanel({ caseId, canUpload = true, uploadLabel = "Upload evidence" }) {
  const [docs, setDocs] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/documents`).then(({ data }) => setDocs(data)).catch(() => {});
  }, [caseId]);

  useEffect(load, [load]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const body = new FormData();
    files.forEach((f) => body.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/documents`, body);
      toast.success(`${data.documents.length} file(s) uploaded`, {
        description: data.arkcase_forwarded ? "Added to the case file" : "Queued for the case file",
      });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="documents-panel" className="space-y-6">
      {canUpload ? (
        <div
          data-testid="evidence-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            upload(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer rounded-xl border border-dashed px-6 py-10 text-center transition-colors ${
            dragging ? "border-gold bg-gold/10" : "border-white/20 bg-white/[0.02] hover:border-white/35"
          }`}
        >
          <UploadCloud className={`mx-auto h-7 w-7 ${dragging ? "text-gold" : "text-[#9CA3AF]"}`} />
          <p className="mt-3 text-sm text-[#F3F4F6]">
            {uploading ? "Uploading…" : <>Drag files here or <span className="text-gold">browse</span></>}
          </p>
          <p className="mt-1 text-xs text-[#6B7280]">{uploadLabel} · PDF, images and documents up to 25MB each</p>
          <input
            ref={inputRef}
            data-testid="evidence-file-input"
            type="file"
            multiple
            className="hidden"
            onChange={(e) => upload(e.target.files)}
          />
        </div>
      ) : null}

      <div className="divide-y divide-white/5 glass rounded-xl overflow-hidden">
        {docs.map((doc) => (
          <div key={doc.id} className="flex items-center gap-4 px-5 py-4" data-testid="document-row">
            <FileText className="h-4 w-4 text-gold shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-[#F3F4F6]">{doc.name}</p>
              <p className="text-xs text-[#6B7280]">
                {doc.category} · {kb(doc.size)} · {doc.uploaded_by} · {shortDate(doc.created_at)}
              </p>
            </div>
            <Button
              data-testid="document-download-btn"
              size="sm"
              variant="ghost"
              className="rounded-full text-[#D1D5DB] hover:bg-white/10 hover:text-gold"
              onClick={() => downloadFile(`/documents/${doc.id}/download`, doc.name).catch(() => toast.error("Download failed"))}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {!docs.length ? <p className="px-5 py-6 text-sm text-[#9CA3AF]">No documents in this case yet.</p> : null}
      </div>
    </div>
  );
}
